/**
 * Damage, shields and regeneration (spec §6). Allocation-free: results go to a caller-owned out object (D-014).
 */
import type { ShipDef } from '../data/schemas'
import { Hull, Shield } from '../world/components'

export interface DamageResult {
  /** Shield points removed by this hit. */
  shieldAbsorbed: number
  /** Hull points removed by this hit (clamped to the hull that was left). */
  hullDamage: number
  /** True when this hit took the shield from above 0 to 0. */
  shieldDown: boolean
  /** True when this hit took the hull from above 0 to 0 (reported once per death). */
  killed: boolean
}

export function createDamageResult(): DamageResult {
  return { shieldAbsorbed: 0, hullDamage: 0, shieldDown: false, killed: false }
}

/**
 * Applies raw damage `raw` to ship `eid` at sim time `now` (seconds). The shield takes `raw × vsShield`;
 * if that exceeds what is left, the leftover raw damage `(raw × vsShield − shield) / vsShield` hits the
 * hull × `vsHull` (a `vsShield` of 0 sends all raw damage to the hull). Every hit restarts the regen delay.
 * A ship already at 0 hull takes no further damage.
 */
export function applyDamage(eid: number, raw: number, vsShield: number, vsHull: number, now: number, out: DamageResult): DamageResult {
  out.shieldAbsorbed = 0
  out.hullDamage = 0
  out.shieldDown = false
  out.killed = false
  const hull = Hull.value[eid]
  if (hull <= 0 || raw <= 0) return out
  Shield.lastHit[eid] = now

  const shield = Shield.value[eid]
  let leftover = raw
  if (vsShield > 0 && shield > 0) {
    const vsShieldDamage = raw * vsShield
    if (vsShieldDamage < shield) {
      Shield.value[eid] = shield - vsShieldDamage
      out.shieldAbsorbed = vsShieldDamage
      return out
    }
    Shield.value[eid] = 0
    out.shieldAbsorbed = shield
    out.shieldDown = true
    leftover = (vsShieldDamage - shield) / vsShield
  }

  const hullDamage = Math.min(leftover * vsHull, hull)
  Hull.value[eid] = hull - hullDamage
  out.hullDamage = hullDamage
  out.killed = hull - hullDamage <= 0
  if (out.killed) Hull.value[eid] = 0
  return out
}

/** Regenerates ship `eid`'s shield at `def.shieldRegen` per second once `def.shieldRegenDelay` has passed since its last hit, capped at `def.shield`. */
export function stepShieldRegen(eid: number, def: ShipDef, dt: number, now: number): void {
  const value = Shield.value[eid]
  if (value >= def.shield) return
  if (now - Shield.lastHit[eid] < def.shieldRegenDelay) return
  const next = value + def.shieldRegen * dt
  Shield.value[eid] = next < def.shield ? next : def.shield
}
